import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Clock, MapPin, Bus, Hourglass } from "lucide-react";
import { api, formatISK } from "@/lib/api";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export default function Tours() {
  const [tours, setTours] = useState([]);

  useEffect(() => {
    api.get("/tours").then((res) => setTours(res.data.tours)).catch(() => {});
  }, []);

  return (
    <div data-testid="tours-page">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-12 border-b border-white/10">
        <motion.p {...fadeUp} className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-5">
          Iceland Expeditions · Oct 1–5
        </motion.p>
        <motion.h1 {...fadeUp} className="font-display font-extrabold uppercase tracking-tighter text-5xl sm:text-6xl lg:text-[5rem] leading-[0.92]">
          Explore Iceland
        </motion.h1>
        <motion.p {...fadeUp} className="text-gray-400 mt-6 max-w-xl leading-relaxed">
          Journey through glacial lagoons, wild highlands and some of Iceland's
          most extraordinary landscapes. Choose from our curated expeditions
          across Iceland's glaciers. Tours either include round-trip transfers to
          and from Viking Village or will meet directly at the village.
        </motion.p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 space-y-6">
        {tours.map((t, i) => (
          <motion.div
            key={t.id}
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: i * 0.06 }}
            className="group grid grid-cols-1 lg:grid-cols-2 border border-white/10 hover:border-white/30 transition-all duration-500 overflow-hidden"
            data-testid={`tour-row-${t.id}`}
          >
            <div className="relative h-72 lg:h-auto overflow-hidden">
              <img src={t.image} alt={t.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0C0C0E]/40" />
            </div>
            <div className="p-8 md:p-12 flex flex-col justify-between bg-[#0C0C0E]">
              <div>
                <h2 className="font-display font-bold uppercase tracking-tight text-3xl leading-tight">{t.name}</h2>
                <p className="text-gray-400 text-sm mt-4 leading-relaxed max-w-md">{t.description}</p>
                <div className="flex flex-wrap gap-6 mt-6 text-xs uppercase tracking-[0.12em] text-gray-500">
                  <span className="inline-flex items-center gap-2"><Clock size={14} /> Activity {t.duration}</span>
                  <span className="inline-flex items-center gap-2"><Hourglass size={14} /> Total {t.total_time}</span>
                  <span className="inline-flex items-center gap-2"><MapPin size={14} /> {t.location}</span>
                </div>
                {t.departure_time && (
                  <p className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-white"><Clock size={14} /> Departs {t.departure_time} from Viking Café</p>
                )}
                {t.transfer_included ? (
                  <p className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-white"><Bus size={14} /> Round-trip transfer included</p>
                ) : (
                  <p className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-gray-500"><MapPin size={14} /> On site at Viking Village</p>
                )}
                <p className="mt-4 text-[11px] leading-relaxed text-gray-600 italic">
                  Estimate departure time. Exact departure times will be confirmed closer to the event and may change due to weather or operational conditions.
                </p>
              </div>
              <div className="flex items-end justify-between mt-8 pt-6 border-t border-white/10">
                <div>
                  <p className="text-2xl font-display font-bold">{formatISK(t.adult_price)}<span className="text-xs text-gray-500 tracking-[0.15em] uppercase ml-2">/ {t.price_unit || "person"}</span></p>
                  {t.has_child && (
                    <p className="text-sm text-gray-400 mt-1">{formatISK(t.child_price)} / child ({t.child_age})</p>
                  )}
                </div>
                {t.coming_soon ? (
                  <span
                    data-testid={`tour-coming-soon-${t.id}`}
                    className="bg-white/5 text-gray-400 border border-white/15 uppercase tracking-[0.15em] text-xs font-bold py-4 px-8 inline-flex items-center gap-3 whitespace-nowrap cursor-not-allowed"
                  >
                    Coming Soon
                  </span>
                ) : (
                  <Link
                    to={`/tours/${t.id}`}
                    data-testid={`tour-book-btn-${t.id}`}
                    className="bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-4 px-8 transition-colors inline-flex items-center gap-3 whitespace-nowrap"
                  >
                    Book <ArrowRight size={15} />
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </section>
    </div>
  );
}
